import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'
import { dateFnsLocale } from '@/utils/dateLocale'
import { getOrder } from '@/api/orders'
import { listSystemConfig } from '@/api/system_config'
import {
  AssignInspectorError,
  assignInspectorToOrder,
  findNearestInspectorsForOrder,
  type NearestInspectorRow,
} from '@/api/nearest_inspectors'
import { useAuthStore } from '@/store/authStore'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { emptyTableCellClassName } from '@/constants/ui'
import { formatPhoneDisplay } from '@/utils/phone'

/** Текст «Обновлено» — без цветовой подсветки (свежесть отображает «Статус»). */
function updatedLabel(iso: string | null): string {
  if (!iso) return i18n.t('geo.positions.noData')
  return formatDistanceToNow(new Date(iso), { locale: dateFnsLocale(), addSuffix: true })
}

/**
 * Pill-бейдж статуса инспектора для колонки «Статус» (стиль из
 * CompanyStatusBadge: rounded-100, uppercase, обводка цветом тона).
 */
const STATUS_PILL_BASE =
  'inline-flex shrink-0 items-center rounded-[100px] border px-2 py-0.5 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] font-medium uppercase tracking-[0.5px]'

const STATUS_PILL_TONE = {
  free:    'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  busy:    'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]',
  missing: 'border-red-500/30 bg-red-500/12 text-red-300',
} as const

/**
 * /admin/orders/:id/nearest-inspectors — подбор и назначение инспектора
 * на заказ в SEARCH. Доступ под `PlatformOnly` (admin + dispatcher);
 * кнопка «Назначить» дополнительно гасится, если строка `is_busy` или
 * `no_position && !isAdminAllowsForce` — здесь оставляем активной по ТЗ.
 *
 * Радиус: initial = `system_config.dispatch_search_radius_km` (фолбэк 50);
 * input + пресеты, изменение мгновенно перезапрашивает RPC.
 */
export function NearestInspectorsPage() {
  const { t } = useTranslation()
  const { id: orderId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const profile = useAuthStore((s) => s.profile)
  const canAssign = profile?.role === 'admin' || profile?.role === 'dispatcher'

  // Системный конфиг для дефолтного радиуса и порога свежести.
  const { data: cfg } = useQuery({
    queryKey: ['system-config'],
    queryFn: listSystemConfig,
    staleTime: 60_000,
  })
  const defaultRadius = useMemo(() => {
    const v = cfg?.find((c) => c.key === 'dispatch_search_radius_km')?.value
    const n = v ? Number(v) : 50
    return Number.isFinite(n) && n > 0 ? Math.min(500, Math.max(10, Math.round(n))) : 50
  }, [cfg])
  // Радиус хранится в state; инициализируем из конфига как только он подъедет.
  const [radiusKm, setRadiusKm] = useState<number | null>(null)
  const effectiveRadius = radiusKm ?? defaultRadius

  // Заказ — для шапки + проверки статуса SEARCH.
  const { data: order, isLoading: loadingOrder, error: orderErr } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => (orderId ? getOrder(orderId) : Promise.resolve(null)),
    enabled: Boolean(orderId),
  })

  // Список ближайших.
  const {
    data: rows,
    isFetching,
    error: rpcErr,
    refetch,
  } = useQuery({
    queryKey: ['nearest-inspectors', orderId, effectiveRadius],
    queryFn: () =>
      orderId
        ? findNearestInspectorsForOrder(orderId, effectiveRadius)
        : Promise.resolve([] as NearestInspectorRow[]),
    enabled: Boolean(orderId && order?.status === 'SEARCH'),
  })

  // Компания инспектора: RPC её не возвращает — догружаем отдельным запросом
  // по найденным `inspector_id`.
  const inspectorIds = useMemo(
    () => (rows ?? []).map((r) => r.inspector_id),
    [rows],
  )
  const { data: companyByInspector } = useQuery({
    queryKey: ['nearest-inspectors-companies', inspectorIds],
    queryFn: async () => {
      if (inspectorIds.length === 0) return new Map<string, string | null>()
      const supabase = createClient()
      const { data, error } = await supabase
        .from('profiles')
        .select('id, company:companies!company_id(name)')
        .in('id', inspectorIds)
      if (error) throw new Error(error.message)
      const m = new Map<string, string | null>()
      for (const r of (data ?? []) as Array<{
        id: string
        company: { name: string | null } | { name: string | null }[] | null
      }>) {
        const c = Array.isArray(r.company) ? r.company[0] : r.company
        m.set(r.id, c?.name ?? null)
      }
      return m
    },
    enabled: inspectorIds.length > 0,
  })

  const [confirming, setConfirming] = useState<NearestInspectorRow | null>(null)
  const [snack, setSnack] = useState<string | null>(null)

  const assignMut = useMutation({
    mutationFn: (inspectorId: string) =>
      assignInspectorToOrder(orderId!, inspectorId),
    onSuccess: () => {
      const name = confirming?.full_name ?? t('geo.nearest.inspectorFallback')
      setSnack(t('geo.nearest.assignSnack', { name }))
      setConfirming(null)
      void qc.invalidateQueries({ queryKey: ['order', orderId] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      // Перенаправляем обратно в карточку заказа (через таймаут, чтобы snack успел показаться).
      setTimeout(() => navigate(`/orders/${orderId}`), 600)
    },
    onError: (e: Error) => {
      const msg =
        e instanceof AssignInspectorError ? translateAssignError(e.code) : e.message
      setSnack(t('geo.positions.errorPrefix', { message: msg }))
      setConfirming(null)
      // На race-condition обновим таблицу — может быть инспектор уже занят.
      void refetch()
    },
  })

  // ── Render ────────────────────────────────────────────────────────────────

  if (!orderId) return <ErrorBanner text={t('geo.nearest.noOrderId')} />
  if (loadingOrder) return <Loader />
  if (orderErr)
    return (
      <ErrorBanner
        text={t('geo.positions.errorPrefix', { message: (orderErr as Error).message })}
      />
    )
  if (!order) return <ErrorBanner text={t('geo.nearest.orderNotFound')} />

  const wrongStatus = order.status !== 'SEARCH'

  return (
    <div>
      <PageHeader
        title={t('geo.nearest.title', { id: order.id.slice(0, 8) })}
        subtitle={
          <span>
            <b>{order.brand} {order.model}</b>
            {' · '}
            {order.address}
            {' · '}
            {order.desired_price != null
              ? `${order.desired_price.toLocaleString('ru-RU')} ₽`
              : '—'}
          </span>
        }
        actions={
          <Button variant="ghost" onClick={() => navigate(`/orders/${order.id}`)}>
            {t('geo.nearest.backToOrder')}
          </Button>
        }
      />

      {wrongStatus ? (
        <ErrorBanner text={t('geo.nearest.wrongStatus', { status: order.status })} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] px-3 py-2">
            <label className="flex items-center gap-2 text-sm text-[color:var(--stryzh-color-text-secondary)]">
              {t('geo.nearest.radiusKm')}
              <input
                type="number"
                min={1}
                max={1000}
                step={5}
                value={effectiveRadius}
                onChange={(e) => {
                  const n = Number(e.target.value)
                  if (!Number.isFinite(n)) return
                  setRadiusKm(Math.min(1000, Math.max(1, Math.round(n))))
                }}
                className="w-20 rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-2 py-1 font-mono text-sm text-[color:var(--stryzh-color-text-primary)]"
              />
            </label>
            <input
              type="range"
              min={1}
              max={500}
              step={1}
              value={Math.min(500, effectiveRadius)}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className="flex-1 max-w-md"
            />
            <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {isFetching
                ? t('geo.nearest.updating')
                : t('geo.nearest.inspectorsCount', { count: rows?.length ?? 0 })}
            </span>
          </div>

          {rpcErr ? (
            (rpcErr as Error).message.includes('order_has_no_coordinates') ? (
              <div className="rounded border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">
                <div className="font-medium">{t('geo.nearest.noCoordsTitle')}</div>
                <div className="mt-1 text-amber-200/80">
                  {t('geo.nearest.noCoordsBody')}
                </div>
                <Link
                  to={`/orders/${order.id}`}
                  className="mt-2 inline-block underline hover:text-amber-50"
                >
                  {t('geo.nearest.noCoordsLink')}
                </Link>
              </div>
            ) : (
              <ErrorBanner
                text={t('geo.nearest.rpcError', { message: (rpcErr as Error).message })}
              />
            )
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
              {isFetching && rows ? <LoadingSecondsBanner /> : null}
              <table className="w-full text-left text-sm">
                <thead className="bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
                  <tr>
                    <th className="px-3 py-2">{t('geo.nearest.col.distance')}</th>
                    <th className="px-3 py-2">{t('geo.nearest.col.fullName')}</th>
                    <th className="px-3 py-2">{t('geo.nearest.col.company')}</th>
                    <th className="px-3 py-2">{t('geo.nearest.col.phone')}</th>
                    <th className="px-3 py-2">{t('geo.nearest.col.updated')}</th>
                    <th className="px-3 py-2">{t('geo.nearest.col.status')}</th>
                    <th className="px-3 py-2">{t('geo.nearest.col.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!rows && isFetching ? (
                    <tr>
                      <td colSpan={7} className={emptyTableCellClassName}>
                        <Loader centered size="sm" />
                      </td>
                    </tr>
                  ) : (rows ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className={emptyTableCellClassName}>
                        {t('common.noData')}
                      </td>
                    </tr>
                  ) : (
                    (rows ?? []).map((row) => {
                      const updated = updatedLabel(row.last_position_at)
                      const company = companyByInspector?.get(row.inspector_id) ?? null
                      const rowCls =
                        'border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)]'
                      const statusTone: keyof typeof STATUS_PILL_TONE = row.is_busy
                        ? 'busy'
                        : row.no_position
                        ? 'missing'
                        : 'free'
                      const statusLabel = row.is_busy
                        ? t('geo.nearest.status.busy')
                        : row.no_position
                        ? t('geo.nearest.status.missing')
                        : t('geo.nearest.status.free')
                      return (
                        <tr key={row.inspector_id} className={rowCls}>
                          <td className="px-3 py-2 font-mono">
                            {row.distance_km != null
                              ? t('geo.nearest.distanceKm', { km: row.distance_km })
                              : '—'}
                          </td>
                          <td className="px-3 py-2 font-medium">
                            <Link
                              to={`/inspectors/${row.inspector_id}/last-position`}
                              className="text-[color:var(--stryzh-color-text-primary)] hover:text-[color:var(--stryzh-color-accent-orange)]"
                              title={t('geo.nearest.openTrack')}
                            >
                              {row.full_name ?? '—'}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                            {company ?? '—'}
                          </td>
                          <td className="px-3 py-2">{formatPhoneDisplay(row.phone)}</td>
                          <td className="px-3 py-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                            {updated}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className={`${STATUS_PILL_BASE} ${STATUS_PILL_TONE[statusTone]}`}>
                                {statusLabel}
                              </span>
                              {row.is_busy && row.busy_order_id ? (
                                <a
                                  href={`/orders/${row.busy_order_id}`}
                                  className="font-mono text-[10px] text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-accent-orange)]"
                                >
                                  {row.busy_order_id.slice(0, 8)}…
                                </a>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              variant="primary"
                              disabled={!canAssign || assignMut.isPending}
                              onClick={() => setConfirming(row)}
                            >
                              {t('geo.nearest.assign')}
                            </Button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {snack ? (
        <div className="fixed bottom-4 right-4 z-[70] rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-primary)] shadow-lg">
          {snack}
          <button
            type="button"
            onClick={() => setSnack(null)}
            className="ml-3 text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]"
            aria-label={t('geo.close')}
          >
            ×
          </button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming != null}
        title={t('geo.nearest.assignTitle')}
        confirmLabel={assignMut.isPending ? t('geo.nearest.assigning') : t('geo.nearest.assign')}
        cancelLabel={t('common.cancel')}
        onCancel={() => (assignMut.isPending ? null : setConfirming(null))}
        onConfirm={() => {
          if (confirming && !assignMut.isPending) {
            assignMut.mutate(confirming.inspector_id)
          }
        }}
      >
        {confirming ? (
          <div className="space-y-2 text-sm text-[color:var(--stryzh-color-text-primary)]">
            <p>
              {t('geo.nearest.assignBodyPre')}{' '}
              <b>{confirming.full_name ?? t('geo.nearest.noNamePlaceholder')}</b>{' '}
              {t('geo.nearest.assignBodyOrder')}{' '}
              <code className="font-mono">{order.id.slice(0, 8)}…</code>?
              <br />
              {t('geo.nearest.assignBodyTail')}
            </p>
            {confirming.is_busy ? (
              <p className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-amber-100">
                {t('geo.nearest.assignBusyWarning')}
              </p>
            ) : null}
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  )
}

function translateAssignError(code: string): string {
  switch (code) {
    case 'order_not_in_search':
      return i18n.t('geo.nearestError.order_not_in_search')
    case 'inspector_already_busy':
      return i18n.t('geo.nearestError.inspector_already_busy')
    case 'not_an_active_inspector':
      return i18n.t('geo.nearestError.not_an_active_inspector')
    case 'inspector_not_found':
      return i18n.t('geo.nearestError.inspector_not_found')
    case 'forbidden':
      return i18n.t('geo.nearestError.forbidden')
    case 'order_has_no_coordinates':
      return i18n.t('geo.nearestError.order_has_no_coordinates')
    default:
      return code
  }
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
      {text}
    </div>
  )
}
